import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { CloseIcon } from 'assets/icons';
import { SwipeableItem } from 'components/commons';
import { SearchUserDialog } from 'components/features/Home';
import SecuredImage from 'components/features/SecuredImage';
import { useLazyLoad } from 'components/hooks';
import { useHeaderContext } from 'components/providers';
import { API_PATHS } from 'constant/api';
import { PAGE_LIST } from 'constant/pages';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getKeyFromLocalStorage } from 'utils/constant';
import { deleteUser, getUsers } from 'utils/fetch';
import ja from 'utils/ja';

interface UserListType {
  id: string;
  displayName: string;
  imageUrl: string;
  affiliation: string;
  tag: string;
}

interface SearchQueryKeyType {
  key: string;
  value: string;
}

export interface QuerySearchUserType {
  displayName: string;
  affiliation: string;
  tag: string;
}

const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAtBottom } = useLazyLoad();

  const headerKey = {
    appId: getKeyFromLocalStorage('appId') ?? '',
    appKey: getKeyFromLocalStorage('appKey') ?? '',
    dbId: getKeyFromLocalStorage('dbId') ?? '',
    dbKey: getKeyFromLocalStorage('dbKey') ?? '',
  };

  const [userList, setUserList] = useState<UserListType[]>([]);
  const [user, setUser] = useState<UserListType>();
  const [stateSearchDialog, setStateSearchDialog] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<SearchQueryKeyType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [query, setQuery] = useState<QuerySearchUserType>({
    displayName: '',
    affiliation: '',
    tag: '',
  });
  const [page] = useState<{ page: number; pageCount: number }>({
    page: 1,
    pageCount: 1,
  });

  const { stateClick, setStateClick } = useHeaderContext();

  const handleChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
    const key = event.target.id as keyof QuerySearchUserType;
    setQuery({ ...query, [key]: event.target.value });
  };

  const handleSearchConditions = () => {
    setStateClick({ click: false, type: '' });
    setStateSearchDialog(false);
    setSearchQuery(
      Object.entries(query)
        .filter(([_key, value]) => value !== '')
        .flatMap(([key, value]) =>
          value.split(',').map((val: string) => ({ key, value: val.trim() }))
        )
        .filter((item) => item.value !== '')
    );
    page.page = 1;
    handleSearch();
  };

  const handleSearch = async (
    pageParam?: number,
    qquery?: QuerySearchUserType
  ) => {
    setLoading(true);
    if (pageParam) page.page = pageParam;
    try {
      const { data, meta } = await getUsers(
        {
          ...(qquery || query),
          page: `${page.page}`,
        },
        headerKey
      );

      setUserList((oldState) => (pageParam ? [...oldState, ...data] : data));
      page.pageCount = meta.pageCount;
    } catch (error) {
      //empty
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSearch = (key: string, value?: string, index?: number) => {
    let qquery = {
      displayName: query.displayName,
      affiliation: query.affiliation,
      tag: query.tag,
    };
    if (key === 'all') {
      setQuery({
        displayName: '',
        affiliation: '',
        tag: '',
      });
      qquery = {
        displayName: '',
        affiliation: '',
        tag: '',
      };
      setSearchQuery([]);
    } else if (key === 'tag') {
      setQuery({
        ...query,
        [key]: query.tag
          .split(',')
          .filter((tag) => tag.trim() !== value)
          .join(','),
      });
      qquery = {
        ...qquery,
        [key]: qquery.tag
          .split(',')
          .filter((tag) => tag.trim() !== value)
          .join(','),
      };
      setSearchQuery((oldState) =>
        oldState.filter((_item, iindex) => iindex !== index)
      );
    } else {
      setQuery({
        ...query,
        [key]: '',
      });
      qquery = {
        ...qquery,
        [key]: '',
      };
      setSearchQuery((oldState) =>
        oldState.filter((_item, iindex) => iindex !== index)
      );
    }
    page.page = 1;
    handleSearch(undefined, qquery);
  };

  const handleToDetail = (id: string) => {
    navigate(PAGE_LIST.USER_DETAIL.pathname(id));
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteUser(user?.id ?? '', headerKey);
      page.page = 1;
      handleSearch();
    } catch (error) {
      //empty
    }
  };

  useEffect(() => {
    handleSearch();
  }, []);

  useEffect(() => {
    if (page.page < page.pageCount && isAtBottom) {
      handleSearch(page.page + 1);
    }
  }, [isAtBottom]);

  useEffect(() => {
    if (stateClick?.click && stateClick.type === 'search') {
      setStateSearchDialog(true);
    }
  }, [stateClick]);

  return (
    <Box
      id='lazy-load'
      width='100%'
      textAlign='right'
      sx={{
        overflowY: 'auto',
        padding: '10px',
        height: { xs: 'calc(100dvh - 60px)', sm: 'calc(100dvh - 80px)' },
        mt: { xs: '60px', sm: '80px' },
      }}
    >
      {!!searchQuery.length && (
        <Stack flexDirection='row' sx={{ mb: '10px', gap: '5px' }}>
          <Stack
            flex='1'
            flexDirection='row'
            alignItems='center'
            gap='5px'
            sx={{
              height: { xs: '54px', sm: '64px' },
              border: '1px solid #79747E',
              borderRadius: '6px',
              padding: '5px',
              overflowX: 'auto',
            }}
          >
            {searchQuery.map((item, index) => (
              <Box
                key={index}
                sx={{
                  height: '100%',
                  pl: '20px',
                  pr: '5px',
                  backgroundColor: '#222831',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '18px', sm: '22px' },
                    fontWeight: 400,
                    color: theme.palette.secondary.light,
                  }}
                >
                  {item.value}
                </Typography>
                <IconButton
                  onClick={() =>
                    handleRemoveSearch(item.key, item.value, index)
                  }
                >
                  <CloseIcon
                    sx={{
                      fontSize: { xs: '25px', sm: '40px' },
                      color: theme.palette.error.main,
                    }}
                  />
                </IconButton>
              </Box>
            ))}
          </Stack>
          <IconButton
            sx={{
              width: { xs: '54px', sm: '64px' },
              height: { xs: '54px', sm: '64px' },
              borderRadius: '4px',
              backgroundColor: '#EBB02D',
            }}
            onClick={() => handleRemoveSearch('all')}
          >
            <CloseIcon
              sx={{
                fontSize: '30px',
                color: theme.palette.secondary.light,
              }}
            />
          </IconButton>
        </Stack>
      )}
      {!userList?.length && !loading && (
        <Typography
          component='p'
          sx={{
            fontSize: { xs: '18px', sm: '36px' },
            textAlign: 'center',
            mt: '20%',
          }}
        >
          {ja.common.not_found}
        </Typography>
      )}
      <Stack gap='10px'>
        {userList.map((user) => (
          <SwipeableItem
            key={user.id}
            handleDelete={handleConfirmDelete}
            handleSelected={() => setUser(user)}
            confirmDeleteTitle={ja.home.dialog.title_delete}
            confirmDeleteContent={i18next.t(ja.home.dialog.content_delete, {
              0: user.displayName,
            })}
          >
            <Stack
              flexDirection='row'
              width='100%'
              gap='10px'
              onClick={() => handleToDetail(user.id)}
            >
              <Box sx={{ display: 'flex' }}>
                <SecuredImage
                  headerKey={headerKey}
                  src={`${API_PATHS.get_avatar_image(user.id)}`}
                  alt='face'
                  width='62px'
                  height='62px'
                />
              </Box>
              <Box
                flex='1'
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  my: '5px',
                }}
              >
                <Typography
                  component='p'
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: '700',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '1',
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {user.displayName}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridAutoFlow: 'row',
                    gridTemplateColumns: '1fr 1.5fr',
                  }}
                >
                  <Typography
                    component='p'
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: '1',
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {user.affiliation}
                  </Typography>
                  <Typography
                    component='p'
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: '1',
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {user.tag}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </SwipeableItem>
        ))}
      </Stack>
      <SearchUserDialog
        data={query}
        open={stateSearchDialog}
        handleClose={() => {
          setStateClick({ click: false, type: '' });
          setStateSearchDialog(false);
        }}
        handleConfirm={handleSearchConditions}
        handleChangeText={handleChangeText}
        handleChangeTag={(key: string, value: string) =>
          setQuery({ ...query, [key]: value })
        }
      />
    </Box>
  );
};

export default Home;
